import {Pipe, PipeTransform} from '@angular/core';
import {DamageListItemStateEnum} from 'src/packages/damage-reporting/data/models/_view/damage-list-item-state.enum';
import {DamageReportDamageStateEnum} from "src/packages/damage-reporting/data/models/damage-report.damage.state.enum";

/**
 * An Angular pipe that converts a {@link DamageListItemStateEnum}
 * into a localized human-readable string with optional icons and colors.
 */
@Pipe({
    name: 'damageState',
    standalone: true,
})
export class DamageStatePipe implements PipeTransform {
    /**
     * Transforms a damage state enum into a localized string, icon name, color name, or ion-icon.
     *
     * @param value - The enum value representing the current damage state.
     * @param mode - The mode indicating what to return: 'iconName', 'colorName', 'label', or 'ionIcon'.
     * @returns A string based on the mode.
     */
    transform(value: DamageListItemStateEnum | DamageReportDamageStateEnum, mode: 'iconName' | 'colorName' | 'label' | 'ionIcon' = 'label'): string {
        const iconMap: Record<DamageListItemStateEnum, string> = {
            [DamageListItemStateEnum.ALL]: 'ellipse',
            [DamageListItemStateEnum.OPEN]: 'ellipse',
            [DamageListItemStateEnum.IN_PROGRESS]: 'ellipsis-horizontal-circle-sharp',
            [DamageListItemStateEnum.FINISHED]: 'checkmark-circle',
            [DamageListItemStateEnum.CANCELLED]: 'close-circle'
        };

        const colorMap: Record<DamageListItemStateEnum, string> = {
            [DamageListItemStateEnum.ALL]: 'primary',
            [DamageListItemStateEnum.OPEN]: 'danger',
            [DamageListItemStateEnum.IN_PROGRESS]: 'warning',
            [DamageListItemStateEnum.FINISHED]: 'success',
            [DamageListItemStateEnum.CANCELLED]: 'tertiary'
        };

        const labelMap: Record<DamageListItemStateEnum, string> = {
            [DamageListItemStateEnum.ALL]: $localize`Alle`,
            [DamageListItemStateEnum.OPEN]: $localize`Offen`,
            [DamageListItemStateEnum.IN_PROGRESS]: $localize`In Bearbeitung`,
            [DamageListItemStateEnum.FINISHED]: $localize`Abgeschlossen`,
            [DamageListItemStateEnum.CANCELLED]: $localize`Storniert`
        };

        switch (mode) {
            case 'iconName':
                return iconMap[value] ?? 'help-circle-outline';
            case 'colorName':
                return colorMap[value] ?? 'medium';
            case 'label':
                return labelMap[value] ?? $localize`Unbekannt`;
            case 'ionIcon':
            default: {
                const icon = `<ion-icon name='${iconMap[value] ?? 'help-circle-outline'}' color='${colorMap[value] ?? 'medium'}'></ion-icon>`;
                const label = labelMap[value] ?? $localize`Unbekannt`;
                return `${icon} ${label}`;
            }
        }
    }
}